import React, { useCallback, useEffect, useState, useRef } from 'react';
import { MoveHorizontal } from 'lucide-react';
interface BeforeAfterSliderProps {
  beforeImage: string;
  afterImage: string;
  label: string;
}
export function BeforeAfterSlider({
  beforeImage,
  afterImage,
  label
}: BeforeAfterSliderProps) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const handleMove = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percent = Math.max(0, Math.min(x / rect.width * 100, 100));
    setSliderPosition(percent);
  }, []);
  const stopDragging = useCallback(() => setIsDragging(false), []);
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      handleMove(e.clientX);
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      handleMove(e.touches[0].clientX);
    };
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('touchmove', handleTouchMove, {
        passive: false
      });
      window.addEventListener('mouseup', stopDragging);
      window.addEventListener('touchend', stopDragging);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('mouseup', stopDragging);
      window.removeEventListener('touchend', stopDragging);
    };
  }, [isDragging, handleMove, stopDragging]);
  return <div className="flex flex-col" data-id="element-28">
      <h3 className="text-xl font-montserrat text-navy mb-4 text-center" data-id="element-29">
        {label}
      </h3>
      <div ref={containerRef} className="relative w-full aspect-video rounded-xl overflow-hidden cursor-ew-resize select-none shadow-card" onMouseDown={e => {
      setIsDragging(true);
      handleMove(e.clientX);
    }} onTouchStart={e => {
      setIsDragging(true);
      handleMove(e.touches[0].clientX);
    }} data-id="element-30">
        {/* After Image (Background) */}
        <img src={afterImage} alt={`${label} After`} className="absolute inset-0 w-full h-full object-cover" draggable="false" data-id="element-31" />

        {/* Before Image (Foreground, clipped) */}
        <div className="absolute inset-0 w-full h-full overflow-hidden" style={{
        clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`
      }} data-id="element-32">
          <img src={beforeImage} alt={`${label} Before`} className="absolute inset-0 w-full h-full object-cover" draggable="false" data-id="element-33" />
        </div>

        {/* Labels */}
        <div className="absolute top-4 left-4 bg-dark/70 text-white px-3 py-1 rounded text-sm font-bold backdrop-blur-sm" data-id="element-34">
          Before
        </div>
        <div className="absolute top-4 right-4 bg-light-blue/90 text-dark px-3 py-1 rounded text-sm font-bold backdrop-blur-sm" data-id="element-35">
          After
        </div>

        {/* Slider Handle */}
        <div className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize shadow-[0_0_10px_rgba(0,0,0,0.5)]" style={{
        left: `${sliderPosition}%`,
        transform: 'translateX(-50%)'
      }} data-id="element-36">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-navy" data-id="element-37">
            <MoveHorizontal className="w-6 h-6" data-id="element-38" />
          </div>
        </div>
      </div>
    </div>;
}